// CareLogix™ ORACLE — Animated Startup Radar (Phase 33 §7)
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";

const BLIPS = [
  { name: "NovaDx",        sector: "Diagnostics",   region: "USA"    },
  { name: "LagosLabs",     sector: "Health IT",     region: "Africa" },
  { name: "VenturaAI",     sector: "AI/ML",         region: "USA"    },
  { name: "KampalaHealth", sector: "Telehealth",    region: "Uganda" },
  { name: "PharosCare",    sector: "Pharma",        region: "EU"     },
  { name: "OptiAfrica",    sector: "Health Fintech",region: "Africa" },
  { name: "LedgerLeaf",    sector: "Med Data",      region: "USA"    },
  { name: "Zenara",        sector: "Digital Health",region: "India"  },
];

export default function StartupRadar({ testid = "startup-radar" }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 90); return () => clearInterval(id); }, []);
  const angle = (tick * 2) % 360;

  const positions = useMemo(() => BLIPS.map((b, i) => {
    const r = 35 + (i * 7) % 45;
    const a = (i * 47) % 360;
    const cx = 110 + r * Math.cos((a * Math.PI) / 180);
    const cy = 110 + r * Math.sin((a * Math.PI) / 180);
    return { ...b, r, a, cx, cy };
  }), []);

  return (
    <section style={S.shell} data-testid={testid} aria-label="Startup Radar">
      <header style={S.head}>
        <div style={S.eyebrow}>STARTUP RADAR™ · LIVE</div>
        <h3 style={S.title}>{BLIPS.length} watchlist candidates</h3>
      </header>
      <div style={S.body}>
        <svg viewBox="0 0 220 220" width="220" height="220" role="img">
          <circle cx="110" cy="110" r="100" fill="none" stroke="rgba(197,160,89,0.20)" />
          <circle cx="110" cy="110" r="70"  fill="none" stroke="rgba(197,160,89,0.14)" />
          <circle cx="110" cy="110" r="40"  fill="none" stroke="rgba(197,160,89,0.10)" />
          <line x1="110" y1="110" x2={110 + 100 * Math.cos((angle * Math.PI) / 180)} y2={110 + 100 * Math.sin((angle * Math.PI) / 180)} stroke="rgba(16,185,129,0.55)" strokeWidth="2" />
          {positions.map((p) => (
            <g key={p.name}>
              <motion.circle cx={p.cx} cy={p.cy} r="4" fill={PALETTE.gold} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2 + (p.r % 3), repeat: Infinity }} />
              <text x={p.cx + 6} y={p.cy + 3} fontSize="8" fill={PALETTE.slate3} fontFamily="'Space Mono', monospace">{p.name}</text>
            </g>
          ))}
        </svg>
        <ul style={S.list}>
          {BLIPS.map((b) => (
            <li key={b.name} style={S.row} data-testid={`${testid}-row-${b.name}`}>
              <span style={S.name}>{b.name}</span>
              <span style={S.meta}>{b.sector} · {b.region}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const S = {
  shell:   { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 8 },
  head:    { display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 },
  eyebrow: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.22em" },
  title:   { margin: 0, color: PALETTE.goldBright, fontSize: 13 },
  body:    { display: "grid", gridTemplateColumns: "220px 1fr", gap: 12, alignItems: "center" },
  list:    { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
  row:     { display: "flex", justifyContent: "space-between", padding: "4px 8px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8 },
  name:    { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  meta:    { color: PALETTE.slate4, fontSize: 11 },
};
